/**
 * HFN Formatter Utility
 *
 * Constructs proper Human-Friendly Names (HFN) using Backend Taxonomy API
 * to convert stored category/subcategory labels to 3-letter codes.
 *
 * This ensures consistent HFN display across Success Page, Asset Detail Page,
 * and Asset Edit Page.
 */
import api from '../http/axiosClient';
import { getBackendUrl } from './environment.config';
import { logger } from './logger';

interface TaxonomyCodeMapping {
  code: string;
  name: string;
  numericCode: string;
}

interface Asset {
  layer: string;
  category?: string;
  categoryCode?: string;
  subcategory?: string;
  subcategoryCode?: string;
  name?: string;
  metadata?: {
    humanFriendlyName?: string;
    hfn?: string;
  };
}

/**
 * Get 3-letter category code from category label using Backend Taxonomy API
 */
async function getCategoryCode(
  layer: string,
  categoryLabel: string
): Promise<string> {
  try {
    const backendUrl = getBackendUrl();
    const response = await api.get(
      `${backendUrl}/taxonomy/layers/${layer}/categories`,
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
    const data = response.data;

    if (data.categories && Array.isArray(data.categories)) {
      // Find category by label (case-insensitive)
      const category = data.categories.find(
        (cat: TaxonomyCodeMapping) =>
          cat.name?.toLowerCase() === categoryLabel.toLowerCase() ||
          cat.code === categoryLabel
      );

      if (category) {
        logger.info(
          `🔍 [HFN FORMATTER] Category mapping: ${categoryLabel} → ${category.code}`
        );
        return category.code;
      }
    }

    logger.warn(
      `⚠️ [HFN FORMATTER] Category not found: ${categoryLabel} in layer ${layer}`
    );
    return categoryLabel; // Fallback to original
  } catch (error) {
    logger.error(
      `❌ [HFN FORMATTER] Failed to get category code for ${categoryLabel}:`,
      error
    );
    return categoryLabel; // Fallback to original
  }
}

/**
 * Get 3-letter subcategory code from subcategory label using Backend Taxonomy API
 */
async function getSubcategoryCode(
  layer: string,
  categoryCode: string,
  subcategoryLabel: string
): Promise<string> {
  try {
    const backendUrl = getBackendUrl();
    const response = await api.get(
      `${backendUrl}/taxonomy/layers/${layer}/categories/${categoryCode}/subcategories`,
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
    const data = response.data;

    if (data.subcategories && Array.isArray(data.subcategories)) {
      // Find subcategory by label (case-insensitive)
      const subcategory = data.subcategories.find(
        (sub: TaxonomyCodeMapping) =>
          sub.name?.toLowerCase() === subcategoryLabel.toLowerCase() ||
          sub.code === subcategoryLabel ||
          (sub.code.includes('.') &&
            sub.code.split('.')[1] === subcategoryLabel)
      );

      if (subcategory) {
        // Extract subcategory code from full code (e.g., "SMT.BAS" → "BAS")
        const subCode = subcategory.code.includes('.')
          ? subcategory.code.split('.')[1]
          : subcategory.code;
        logger.info(
          `🔍 [HFN FORMATTER] Subcategory mapping: ${subcategoryLabel} → ${subCode}`
        );
        return subCode;
      }
    }

    logger.warn(
      `⚠️ [HFN FORMATTER] Subcategory not found: ${subcategoryLabel} in ${layer}.${categoryCode}`
    );
    return subcategoryLabel; // Fallback to original
  } catch (error) {
    logger.error(
      `❌ [HFN FORMATTER] Failed to get subcategory code for ${subcategoryLabel}:`,
      error
    );
    return subcategoryLabel; // Fallback to original
  }
}

/**
 * Extract sequential number from asset name
 */
function extractSequentialNumber(assetName: string): string {
  if (!assetName) return '001';

  // For composite assets: C.FUL.HIP.002:1.001.001.002+2.001.001.002+...
  if (assetName.includes(':')) {
    const baseHfn = assetName.split(':')[0];
    const match = baseHfn.match(/\.(\d{3})$/);
    return match ? match[1] : '001';
  }

  // For simple assets: G.POP.BAS.003
  const match = assetName.match(/\.(\d{3})$/);
  return match ? match[1] : '001';
}

/**
 * Format proper HFN using Backend Taxonomy API to convert labels to 3-letter codes
 */
export async function formatHFN(asset: Asset): Promise<string> {
  try {
    // If we already have a proper HFN in metadata, use it
    if (asset.metadata?.humanFriendlyName || asset.metadata?.hfn) {
      const existingHFN =
        asset.metadata.humanFriendlyName || asset.metadata.hfn;
      if (
        existingHFN &&
        /^[A-Z]\.[A-Z]{3}\.[A-Z]{3}\.\d{3}$/.test(existingHFN)
      ) {
        logger.info(`✅ [HFN FORMATTER] Using existing HFN: ${existingHFN}`);
        return existingHFN;
      }
    }

    const layer = asset.layer;
    const category = asset.category || asset.categoryCode || '';
    const subcategory = asset.subcategory || asset.subcategoryCode || '';

    if (!layer || !category || !subcategory) {
      logger.warn(
        `⚠️ [HFN FORMATTER] Missing required fields: layer=${layer}, category=${category}, subcategory=${subcategory}`
      );
      return asset.name || 'Unknown';
    }

    logger.info(
      `🚀 [HFN FORMATTER] Processing asset: ${layer}.${category}.${subcategory}`
    );

    // Convert category label to 3-letter code if needed
    let categoryCode = category;
    if (category.length > 3 || category.includes('_')) {
      categoryCode = await getCategoryCode(layer, category);
    }

    // Convert subcategory label to 3-letter code if needed
    let subcategoryCode = subcategory;
    if (subcategory.length > 3 || subcategory.includes('_')) {
      subcategoryCode = await getSubcategoryCode(
        layer,
        categoryCode,
        subcategory
      );
    }

    // Extract actual sequential number from asset name instead of hardcoding .001
    const sequentialNumber = extractSequentialNumber(asset.name || '');
    const hfn = `${layer}.${categoryCode}.${subcategoryCode}.${sequentialNumber}`;

    logger.info(
      `✅ [HFN FORMATTER] Generated HFN: ${hfn} (sequential: ${sequentialNumber})`
    );
    return hfn;
  } catch (error) {
    logger.error(`❌ [HFN FORMATTER] Failed to format HFN for asset:`, error);
    return asset.name || 'Unknown';
  }
}

/**
 * Synchronous version that constructs HFN from already-available 3-letter codes
 */
export function formatHFNFromCodes(
  layer: string,
  categoryCode: string,
  subcategoryCode: string,
  sequentialNumber: string = '001'
): string {
  return `${layer}.${categoryCode}.${subcategoryCode}.${sequentialNumber}`;
}
